import type {
  AgentSessionEvent,
  AgentSessionEventContentBlock,
  AgentSessionEventMeta,
} from "../models/AgentSession";

/**
 * 事件语义分类（用于卡片分发）。
 */
export type AgentEventKind =
  | "message"
  | "session_status"
  | "model_request_start"
  | "model_request_end"
  | "mcp_call"
  | "mcp_call_output"
  | "unknown";

/**
 * content 块归一化结果（保留原始 type，并挂上解码后的 data）。
 */
export interface NormalizedContentBlock {
  type: string;
  text: string;
  /** 原始 data（可能未解码） */
  data?: Record<string, unknown>;
  /** 递归解码后的 data */
  decodedData?: Record<string, unknown>;
}

/**
 * 从任意 JSON 树中抽出的可渲染片段。
 */
export interface ExtractedParts {
  /** 人类可读文本行 */
  texts: string[];
  /** 结构化对象（线索、resultsWithId 等） */
  objects: unknown[];
  /** 扁平关键字段（已尽量解码） */
  fields: Record<string, unknown>;
  isError: boolean;
}

/**
 * 卡片展示用已解析字段。
 */
export interface AgentEventDisplay {
  model: string;
  requestId: string;
  modelRequestId: string;
  tokens: { input: number; output: number } | null;
  sessionStatus: string;
  stopReason: string;
  toolName: string;
  serverLabel: string;
  callId: string;
  argumentsPretty: string;
  outputPretty: string;
  mcpSummaryLines: string[];
  mcpPayloads: unknown[];
  /** data 中其余关键字段的漂亮打印，兜底展示 */
  extraDataPretty: string;
  isError: boolean;
}

/**
 * 解析后的统一事件视图。
 */
export interface ParsedAgentEvent {
  id: string;
  kind: AgentEventKind;
  rawType: string;
  role: string;
  createdAt: string;
  status: string;
  text: string;
  content: AgentSessionEventContentBlock[];
  blocks: NormalizedContentBlock[];
  meta: AgentSessionEventMeta;
  display: AgentEventDisplay;
  /** 合并并深度解码后的 data 载荷 */
  data: Record<string, unknown>;
  raw: Record<string, unknown>;
}

/** 常见“应尝试 JSON 解码”的字段名 */
const JSONISH_KEYS = new Set([
  "output",
  "arguments",
  "args",
  "input",
  "result",
  "results",
  "payload",
  "body",
  "content",
  "data",
  "message",
  "text",
  "structuredContent",
  "structured_content",
]);

/**
 * 将未知值转为普通对象。
 */
export function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

/**
 * 安全取字符串。
 */
export function pickStr(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

/**
 * 安全取数字。
 */
export function pickNum(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/**
 * 清洗常见脏 JSON 字符串（数组元素间多余换行、尾逗号等）。
 */
function sanitizeJsonString(raw: string): string {
  return raw
    .replace(/:\s*\n\s*\[/g, ": [")
    .replace(/\]\s*\n\s*,/g, "],")
    .replace(/,\s*\n\s*,/g, ",")
    .replace(/,\s*([}\]])/g, "$1")
    .replace(/\n/g, " ");
}

/**
 * 判断字符串是否像 JSON。
 */
function looksLikeJson(trimmed: string): boolean {
  return (
    (trimmed.startsWith("{") && trimmed.endsWith("}"))
    || (trimmed.startsWith("[") && trimmed.endsWith("]"))
    || (trimmed.startsWith('"') && trimmed.endsWith('"'))
  );
}

/**
 * 尝试解析 JSON；对象/数组原样返回；字符串最多递归 depth 层。
 */
export function tryParseJson(value: unknown, depth = 4): unknown {
  if (depth < 0) {
    return value;
  }
  if (typeof value === "object" && value !== null) {
    return value;
  }
  if (typeof value !== "string") {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed || !looksLikeJson(trimmed)) {
    return value;
  }
  try {
    return tryParseJson(JSON.parse(trimmed), depth - 1);
  } catch {
    try {
      return tryParseJson(JSON.parse(sanitizeJsonString(trimmed)), depth - 1);
    } catch {
      return value;
    }
  }
}

/**
 * 深度解码：遍历对象/数组，对疑似 JSON 字符串字段递归 parse。
 */
export function deepDecode(value: unknown, depth = 4): unknown {
  if (depth < 0) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = tryParseJson(value, depth);
    if (parsed !== value) {
      return deepDecode(parsed, depth - 1);
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepDecode(item, depth - 1));
  }
  const record = asRecord(value);
  if (!record) {
    return value;
  }
  const next: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(record)) {
    if (typeof child === "string" && (JSONISH_KEYS.has(key) || looksLikeJson(child.trim()))) {
      next[key] = deepDecode(tryParseJson(child, depth), depth - 1);
    } else {
      next[key] = deepDecode(child, depth - 1);
    }
  }
  return next;
}

/**
 * 漂亮打印任意值。
 */
export function prettyJson(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string") {
    const parsed = tryParseJson(value);
    if (parsed !== value) {
      return prettyJson(parsed);
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * 归一化 content 块：兼容 type=text|data|其他，data 可为对象或 JSON 字符串。
 */
export function normalizeContentBlocks(content: unknown): NormalizedContentBlock[] {
  const list: unknown[] = Array.isArray(content)
    ? content
    : content === null || content === undefined
      ? []
      : [content];

  return list
    .map((block): NormalizedContentBlock | null => {
      if (typeof block === "string") {
        const parsed = tryParseJson(block);
        const asObj = asRecord(parsed);
        if (asObj && (asObj.type || asObj.data || asObj.text)) {
          return normalizeContentBlocks([asObj])[0] ?? null;
        }
        return { type: "text", text: block };
      }
      const record = asRecord(block);
      if (!record) {
        return null;
      }

      // data / payload / json 可能是对象，也可能是 JSON 字符串
      let data: Record<string, unknown> | undefined =
        asRecord(record.data)
        ?? asRecord(record.payload)
        ?? asRecord(record.json)
        ?? undefined;
      if (!data && typeof record.data === "string") {
        data = asRecord(tryParseJson(record.data)) ?? undefined;
      }
      if (!data && typeof record.payload === "string") {
        data = asRecord(tryParseJson(record.payload)) ?? undefined;
      }
      if (!data && typeof record.json === "string") {
        data = asRecord(tryParseJson(record.json)) ?? undefined;
      }
      // 少数形态：整块就是 payload，没有 data 包裹
      if (
        !data
        && (record.output !== undefined
          || record.arguments !== undefined
          || record.args !== undefined
          || record.name !== undefined
          || record.input_tokens !== undefined
          || record.session_status !== undefined)
      ) {
        data = record;
      }

      const decodedData = data
        ? (asRecord(deepDecode(data)) ?? data)
        : undefined;

      const rawType = pickStr(record.type).toLowerCase();
      const type =
        pickStr(record.type)
        || (decodedData ? "data" : "")
        || (pickStr(record.text) ? "text" : "unknown");

      // text / output_text / input_text / json 等块的文本抽取
      let text = pickStr(
        record.text
          ?? record.content
          ?? record.value
          ?? record.output_text
          ?? record.input_text
          ?? decodedData?.text
      );
      if (!text && (rawType === "json" || rawType === "data") && decodedData) {
        text = "";
      }
      if (!text && (rawType === "image" || rawType === "image_url")) {
        text = pickStr(record.url ?? record.image_url ?? decodedData?.url) || "[图片]";
      }
      if (!text && (rawType === "file" || rawType === "document")) {
        text = pickStr(record.filename ?? record.name ?? decodedData?.filename) || "[文件]";
      }

      return {
        type,
        text,
        data: data ?? undefined,
        decodedData,
      };
    })
    .filter((item): item is NormalizedContentBlock => Boolean(item));
}

/**
 * 兼容旧 ContentBlock 类型。
 */
export function extractContentBlocks(content: unknown): AgentSessionEventContentBlock[] {
  return normalizeContentBlocks(content).map((block) => ({
    type: block.type,
    text: block.text,
    data: block.decodedData ?? block.data,
  }));
}

/**
 * 合并所有 content 块的 data（优先 decoded），再回退顶层字段。
 */
export function collectDataPayload(
  event: Record<string, unknown>,
  blocks?: NormalizedContentBlock[]
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};
  const normalized = blocks ?? normalizeContentBlocks(event.content ?? event.contents);

  for (const block of normalized) {
    const payload = block.decodedData ?? block.data;
    if (payload) {
      Object.assign(merged, payload);
    }
  }

  const topDataRaw = event.data;
  const topData =
    asRecord(topDataRaw)
    ?? asRecord(tryParseJson(topDataRaw));
  if (topData) {
    Object.assign(merged, asRecord(deepDecode(topData)) ?? topData);
  }

  // 顶层兜底
  const fallbackKeys = [
    "model",
    "span_id",
    "spanId",
    "request_id",
    "requestId",
    "model_request_id",
    "usage",
    "input_tokens",
    "output_tokens",
    "cache_read_input_tokens",
    "cache_creation_input_tokens",
    "name",
    "tool_name",
    "arguments",
    "args",
    "input",
    "output",
    "result",
    "call_id",
    "tool_use_id",
    "server_label",
    "session_status",
    "status",
    "stop_reason",
    "is_error",
    "isError",
  ];
  for (const key of fallbackKeys) {
    if (merged[key] === undefined && event[key] !== undefined) {
      const decoded = deepDecode(event[key]);
      merged[key] = decoded;
    }
  }

  return asRecord(deepDecode(merged)) ?? merged;
}

/**
 * 从任意值中递归抽取可展示片段（文本 / 对象 / 错误标记）。
 */
export function extractParts(value: unknown, depth = 5): ExtractedParts {
  const texts: string[] = [];
  const objects: unknown[] = [];
  const fields: Record<string, unknown> = {};
  let isError = false;

  const visit = (node: unknown, path: string, d: number) => {
    if (d < 0 || node === null || node === undefined) {
      return;
    }
    if (typeof node === "boolean") {
      if (path.endsWith("isError") || path.endsWith("is_error")) {
        isError = isError || node;
      }
      return;
    }
    if (typeof node === "number") {
      if (path) {
        fields[path] = node;
      }
      return;
    }
    if (typeof node === "string") {
      const parsed = tryParseJson(node);
      if (parsed !== node) {
        visit(parsed, path, d - 1);
        return;
      }
      const trimmed = node.trim();
      if (trimmed) {
        texts.push(trimmed);
      }
      return;
    }
    if (Array.isArray(node)) {
      // MCP content 数组：优先按 text 块处理（text 可能已是解码后的对象）
      for (const item of node) {
        const rec = asRecord(item);
        if (rec && (rec.type === "text" || rec.text !== undefined || rec.type === "output_text")) {
          const textValue = rec.text ?? rec.content ?? rec.value;
          if (typeof textValue === "object" && textValue !== null) {
            visit(textValue, path, d - 1);
            continue;
          }
          const t = pickStr(textValue);
          if (!t) {
            continue;
          }
          const inner = tryParseJson(t);
          if (typeof inner === "object" && inner !== null) {
            objects.push(inner);
            const innerRec = asRecord(inner);
            if (innerRec && Array.isArray(innerRec.resultsWithId)) {
              texts.push(
                pickStr(innerRec.itemCount)
                  ? `查询到 ${innerRec.itemCount} 条结果`
                  : `查询到 ${innerRec.resultsWithId.length} 条结果`
              );
            }
          } else {
            texts.push(t);
          }
        } else {
          visit(item, path, d - 1);
        }
      }
      return;
    }

    const rec = asRecord(node);
    if (!rec) {
      return;
    }

    if (rec.isError === true || rec.is_error === true) {
      isError = true;
    }

    // 有 resultsWithId 的聚合结果
    if (Array.isArray(rec.resultsWithId)) {
      objects.push(rec);
      texts.push(
        pickStr(rec.itemCount)
          ? `查询到 ${rec.itemCount} 条结果`
          : `查询到 ${rec.resultsWithId.length} 条结果`
      );
      return;
    }

    // 线索对象
    if (rec.company_name || rec.companyName) {
      objects.push(rec);
      return;
    }

    for (const [key, child] of Object.entries(rec)) {
      const nextPath = path ? `${path}.${key}` : key;
      if (key === "content" && Array.isArray(child)) {
        visit(child, nextPath, d - 1);
        continue;
      }
      if (key === "meta" || key === "annotations" || key === "structuredContent" || key === "structured_content") {
        // 跳过噪声；structuredContent 若有值再收录
        if (key.startsWith("structured") && child) {
          visit(child, nextPath, d - 1);
        }
        continue;
      }
      if (
        typeof child === "string"
        || typeof child === "number"
        || typeof child === "boolean"
        || child === null
      ) {
        if (JSONISH_KEYS.has(key) && typeof child === "string") {
          visit(tryParseJson(child), nextPath, d - 1);
        } else if (typeof child === "string" && looksLikeJson(child.trim())) {
          visit(tryParseJson(child), nextPath, d - 1);
        } else if (typeof child === "string" && child.trim()) {
          // output/arguments 的纯文本也保留到 fields，便于 UI
          fields[key] = child;
          if (key === "text" || key === "message") {
            texts.push(child.trim());
          }
        } else if (typeof child !== "string") {
          fields[key] = child;
        }
        continue;
      }
      visit(child, nextPath, d - 1);
    }
  };

  visit(deepDecode(value), "", depth);
  return {
    texts: Array.from(new Set(texts)),
    objects,
    fields,
    isError,
  };
}

/**
 * 专门展开 MCP output / arguments 等字段。
 */
export function flattenJsonishField(value: unknown): ExtractedParts {
  return extractParts(value);
}

/**
 * 识别事件 kind。
 */
function resolveKind(rawType: string): AgentEventKind {
  const t = rawType.toLowerCase();
  if (t.includes("mcp_call_output") || t.includes("tool_call_output") || (t.includes("tool_result") && !t.includes("tool_use"))) {
    return "mcp_call_output";
  }
  if (t.includes("mcp_call") || t.includes("tool_call") || t.includes("tool_use")) {
    return "mcp_call";
  }
  if (t.includes("model_request_start")) {
    return "model_request_start";
  }
  if (t.includes("model_request_end")) {
    return "model_request_end";
  }
  if (t.includes("session_status") || t.includes("status_running") || t.includes("status_idle")) {
    return "session_status";
  }
  if (
    t === "message"
    || t.includes("user.message")
    || t.includes("agent.message")
    || t.includes("thread_message")
  ) {
    return "message";
  }
  return "unknown";
}

function emptyMeta(): AgentSessionEventMeta {
  return {
    model: "",
    spanId: "",
    toolName: "",
    toolUseId: "",
    toolInput: "",
    toolOutput: "",
    isError: false,
    sessionStatus: "",
    stopReason: "",
    usage: null,
    callId: "",
    serverLabel: "",
    requestId: "",
    modelRequestId: "",
    mcpSummaryLines: [],
    mcpPayloads: [],
    argumentsPretty: "",
    outputPretty: "",
    extraDataPretty: "",
  };
}

function emptyDisplay(): AgentEventDisplay {
  return {
    model: "",
    requestId: "",
    modelRequestId: "",
    tokens: null,
    sessionStatus: "",
    stopReason: "",
    toolName: "",
    serverLabel: "",
    callId: "",
    argumentsPretty: "",
    outputPretty: "",
    mcpSummaryLines: [],
    mcpPayloads: [],
    extraDataPretty: "",
    isError: false,
  };
}

function parseStopReason(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  const record = asRecord(value);
  return record ? pickStr(record.type ?? record.reason ?? record.message) : "";
}

/**
 * 生成 data 中“未消费”字段的兜底漂亮打印。
 */
function buildExtraDataPretty(
  data: Record<string, unknown>,
  consumedKeys: string[]
): string {
  const consumed = new Set(consumedKeys);
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (consumed.has(key)) {
      continue;
    }
    if (value === null || value === undefined || value === "") {
      continue;
    }
    rest[key] = value;
  }
  return Object.keys(rest).length > 0 ? prettyJson(rest) : "";
}

/**
 * 解析单条智能体会话事件（尽量覆盖 content/data 的各种不一致形态）。
 */
export function parseAgentEvent(value: unknown): ParsedAgentEvent {
  const raw = asRecord(value) ?? {};
  const rawType = pickStr(raw.type ?? raw.eventType ?? raw.event_type);
  const kind = resolveKind(rawType);
  const blocks = normalizeContentBlocks(raw.content ?? raw.contents);
  const data = collectDataPayload(raw, blocks);
  const status = pickStr(raw.status);
  const role = pickStr(
    raw.role
      ?? raw.sender
      ?? (rawType.toLowerCase().includes("user")
        ? "user"
        : rawType.toLowerCase().includes("assistant")
          ? "assistant"
          : rawType.toLowerCase().includes("tool")
            ? "tool"
            : "")
  );
  const id = pickStr(raw.id ?? raw._id ?? raw.eventId ?? raw.event_id);
  const createdAt = pickStr(
    raw.createdAt
      ?? raw.created_at
      ?? raw.processedAt
      ?? raw.processed_at
      ?? raw.timestamp
      ?? raw.time
  );

  const meta = emptyMeta();
  const display = emptyDisplay();
  const textBlocks = blocks.filter((b) => b.type === "text" || Boolean(b.text)).map((b) => b.text).filter(Boolean);
  let text = textBlocks.join("\n");
  const consumed: string[] = [];

  // 通用：arguments / output 无论事件类型都先解码，便于兜底展示
  const argsSource = data.arguments ?? data.args ?? data.input;
  const outputSource = data.output ?? data.result;
  if (argsSource !== undefined) {
    const argsDecoded = deepDecode(tryParseJson(argsSource));
    meta.argumentsPretty = prettyJson(argsDecoded);
    meta.toolInput = meta.argumentsPretty;
    display.argumentsPretty = meta.argumentsPretty;
    consumed.push("arguments", "args", "input");
  }
  if (outputSource !== undefined) {
    const outputParts = flattenJsonishField(outputSource);
    meta.mcpSummaryLines = outputParts.texts;
    meta.mcpPayloads = outputParts.objects;
    meta.outputPretty = prettyJson(deepDecode(tryParseJson(outputSource)));
    meta.toolOutput =
      outputParts.texts.join("\n")
      || (outputParts.objects[0] ? prettyJson(outputParts.objects[0]) : meta.outputPretty);
    display.mcpSummaryLines = outputParts.texts;
    display.mcpPayloads = outputParts.objects;
    display.outputPretty = meta.outputPretty;
    display.isError = display.isError || outputParts.isError;
    meta.isError = meta.isError || outputParts.isError;
    consumed.push("output", "result");
  }

  if (kind === "message") {
    if (!text) {
      text = pickStr(raw.text ?? raw.message ?? raw.body);
    }
    // 若 message 误带 data 文本
    if (!text && data.text) {
      text = pickStr(data.text);
    }
  }

  if (kind === "session_status") {
    const sessionStatus = pickStr(
      data.session_status ?? data.sessionStatus ?? data.status ?? raw.session_status
    );
    const stopReason = parseStopReason(data.stop_reason ?? data.stopReason ?? raw.stop_reason);
    meta.sessionStatus = sessionStatus;
    meta.stopReason = stopReason;
    display.sessionStatus = sessionStatus;
    display.stopReason = stopReason;
    text = text || `会话状态：${sessionStatus}${stopReason ? `（${stopReason}）` : ""}`;
    consumed.push("session_status", "sessionStatus", "status", "stop_reason", "stopReason");

    const usageNested = asRecord(data.usage ?? data.model_usage);
    const inputTokens = pickNum(
      data.input_tokens ?? data.inputTokens ?? usageNested?.input_tokens ?? usageNested?.inputTokens
    );
    const outputTokens = pickNum(
      data.output_tokens
        ?? data.outputTokens
        ?? usageNested?.output_tokens
        ?? usageNested?.outputTokens
    );
    const cacheRead = pickNum(
      data.cache_read_input_tokens
        ?? usageNested?.cache_read_input_tokens
        ?? usageNested?.cacheReadInputTokens
    );
    const cacheCreate = pickNum(
      data.cache_creation_input_tokens
        ?? usageNested?.cache_creation_input_tokens
        ?? usageNested?.cacheCreationInputTokens
    );
    const hasTokens = inputTokens > 0 || outputTokens > 0 || cacheRead > 0 || cacheCreate > 0;
    if (hasTokens) {
      meta.usage = {
        inputTokens,
        outputTokens,
        cacheReadInputTokens: cacheRead,
        cacheCreationInputTokens: cacheCreate,
      };
      display.tokens = { input: inputTokens, output: outputTokens };
      consumed.push(
        "usage",
        "model_usage",
        "input_tokens",
        "output_tokens",
        "inputTokens",
        "outputTokens",
        "cache_read_input_tokens",
        "cache_creation_input_tokens"
      );
    }
  }

  if (kind === "model_request_start" || kind === "model_request_end") {
    const modelVal = data.model;
    const model =
      pickStr(modelVal)
      || pickStr(asRecord(modelVal)?.id)
      || pickStr(asRecord(modelVal)?.name)
      || pickStr(raw.model);
    const requestId = pickStr(data.request_id ?? data.requestId ?? raw.request_id);
    meta.model = model;
    meta.requestId = requestId;
    meta.spanId = requestId;
    display.model = model;
    display.requestId = requestId;
    consumed.push("model", "request_id", "requestId");

    if (kind === "model_request_start") {
      text = text || (model ? `模型请求开始：${model}` : "模型请求开始");
    }

    if (kind === "model_request_end") {
      const modelRequestId = pickStr(data.model_request_id ?? data.modelRequestId);
      const usageNested = asRecord(data.usage ?? data.model_usage);
      const inputTokens = pickNum(
        data.input_tokens ?? data.inputTokens ?? usageNested?.input_tokens ?? usageNested?.inputTokens
      );
      const outputTokens = pickNum(
        data.output_tokens
          ?? data.outputTokens
          ?? usageNested?.output_tokens
          ?? usageNested?.outputTokens
      );
      const cacheRead = pickNum(
        data.cache_read_input_tokens
          ?? usageNested?.cache_read_input_tokens
          ?? usageNested?.cacheReadInputTokens
      );
      const cacheCreate = pickNum(
        data.cache_creation_input_tokens
          ?? usageNested?.cache_creation_input_tokens
          ?? usageNested?.cacheCreationInputTokens
      );
      const isError = data.is_error === true || data.isError === true;
      const hasTokens = inputTokens > 0 || outputTokens > 0 || cacheRead > 0 || cacheCreate > 0;
      meta.modelRequestId = modelRequestId;
      meta.spanId = modelRequestId || requestId;
      meta.isError = isError;
      meta.usage = hasTokens
        ? {
            inputTokens,
            outputTokens,
            cacheReadInputTokens: cacheRead,
            cacheCreationInputTokens: cacheCreate,
          }
        : null;
      display.modelRequestId = modelRequestId;
      display.isError = isError;
      display.tokens = hasTokens ? { input: inputTokens, output: outputTokens } : null;
      text =
        text
        || (hasTokens
          ? `模型请求结束 · in ${inputTokens} / out ${outputTokens}`
          : "模型请求结束");
      consumed.push(
        "model_request_id",
        "modelRequestId",
        "usage",
        "model_usage",
        "input_tokens",
        "output_tokens",
        "inputTokens",
        "outputTokens",
        "cache_read_input_tokens",
        "cache_creation_input_tokens",
        "is_error",
        "isError"
      );
    }
  }

  if (kind === "mcp_call" || kind === "mcp_call_output") {
    const toolName = pickStr(data.name ?? data.tool_name ?? data.toolName);
    const serverLabel = pickStr(data.server_label ?? data.serverLabel);
    const callId = pickStr(
      data.call_id ?? data.callId ?? data.tool_use_id ?? data.toolUseId
    );
    meta.toolName = toolName;
    meta.serverLabel = serverLabel;
    meta.callId = callId;
    meta.toolUseId = callId;
    display.toolName = toolName;
    display.serverLabel = serverLabel;
    display.callId = callId;
    consumed.push(
      "name",
      "tool_name",
      "toolName",
      "server_label",
      "serverLabel",
      "call_id",
      "callId",
      "tool_use_id",
      "toolUseId"
    );

    if (kind === "mcp_call") {
      text = text || (toolName ? `调用工具：${toolName}` : "MCP 工具调用");
    } else {
      // 若尚未从 output 抽出摘要，再扫一遍整份 data
      if (meta.mcpSummaryLines.length === 0 && meta.mcpPayloads.length === 0) {
        const parts = extractParts(data);
        meta.mcpSummaryLines = parts.texts;
        meta.mcpPayloads = parts.objects;
        meta.isError = meta.isError || parts.isError;
        display.mcpSummaryLines = parts.texts;
        display.mcpPayloads = parts.objects;
        display.isError = meta.isError;
      }
      text =
        text
        || meta.mcpSummaryLines.join("\n")
        || (toolName ? `${toolName} 输出` : "MCP 工具输出");
    }
  }

  if (kind === "unknown" || kind === "message") {
    // 未知 / 消息：再扫一遍，补上可能夹带的结构化片段
    const parts = extractParts({
      texts: textBlocks,
      data,
      blocks: blocks.map((b) => b.decodedData ?? b.data ?? { text: b.text }),
    });
    if (kind === "unknown") {
      if (!text) {
        text =
          parts.texts.join("\n")
          || meta.argumentsPretty
          || meta.outputPretty
          || (Object.keys(data).length ? prettyJson(data) : "")
          || rawType
          || "未知事件";
      }
      meta.mcpSummaryLines = parts.texts.length ? parts.texts : meta.mcpSummaryLines;
      meta.mcpPayloads = parts.objects.length ? parts.objects : meta.mcpPayloads;
      meta.isError = meta.isError || parts.isError;
      display.mcpSummaryLines = meta.mcpSummaryLines;
      display.mcpPayloads = meta.mcpPayloads;
      display.isError = meta.isError;
    } else if (!text && parts.texts.length) {
      text = parts.texts.join("\n");
    }
  }

  // 错误类 type 兜底
  if (
    rawType.toLowerCase().includes("error")
    || data.is_error === true
    || data.isError === true
  ) {
    meta.isError = true;
    display.isError = true;
    consumed.push("is_error", "isError");
  }

  const extraDataPretty = buildExtraDataPretty(data, consumed);
  meta.extraDataPretty = extraDataPretty;
  display.extraDataPretty = extraDataPretty;

  // 若没有任何文本但有 arguments/output，保证气泡有可读内容
  if (!text) {
    text =
      meta.mcpSummaryLines.join("\n")
      || meta.argumentsPretty
      || meta.outputPretty
      || extraDataPretty
      || rawType;
  }

  return {
    id,
    kind,
    rawType,
    role,
    createdAt,
    status,
    text,
    content: extractContentBlocks(raw.content ?? raw.contents),
    blocks,
    meta,
    display,
    data,
    raw,
  };
}

/**
 * 将解析结果映射为列表/UI 使用的 AgentSessionEvent。
 */
export function toAgentSessionEvent(parsed: ParsedAgentEvent): AgentSessionEvent {
  return {
    id: parsed.id,
    type: parsed.rawType,
    role: parsed.role,
    text: parsed.text,
    content: parsed.content,
    processedAt: parsed.createdAt,
    createdAt: parsed.createdAt,
    meta: parsed.meta,
    raw: parsed.raw,
  };
}

/**
 * 一步：原始事件 → AgentSessionEvent。
 */
export function normalizeParsedAgentSessionEvent(value: unknown): AgentSessionEvent {
  return toAgentSessionEvent(parseAgentEvent(value));
}

/** @deprecated 使用 flattenJsonishField / extractParts */
export function flattenMcpOutput(output: unknown): {
  summaryTexts: string[];
  payloadObjects: unknown[];
  isError: boolean;
} {
  const parts = flattenJsonishField(output);
  return {
    summaryTexts: parts.texts,
    payloadObjects: parts.objects,
    isError: parts.isError,
  };
}
